import { tva } from '@gluestack-ui/nativewind-utils/tva';
import * as Linking from 'expo-linking';
import { HelpCircleIcon, type LucideIcon } from 'lucide-react-native';
import { cssInterop } from 'nativewind';
import type { ReactNode } from 'react';
import { Animated, View } from 'react-native';
import Linkify from 'react-native-linkify';

import { Icon } from './ui/icon';
import { Pressable } from './ui/pressable';
import { Toast, ToastDescription, ToastTitle } from './ui/toast';
import { VStack } from './ui/vstack';

import { useSwipeUpDismiss } from '@/hooks/useSwipeUpDismiss';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/** Names the swipe for readers who cannot perform it. */
const DISMISS_ACTION_NAME = 'dismiss';

const StyledLinkify = cssInterop(Linkify, {
  linkClassName: 'linkStyle',
});

/**
 * The icon is what tells the toast types apart, so it carries the tint and the
 * title stays dark. Each shade is the lightest that clears the 3:1 non-text
 * minimum on the toast's white surface in both themes; the 500 stops did not
 * in light mode, and nor did the tinted titles they replaced.
 */
const iconStyle = tva({
  base: 'mt-0.5',
  variants: {
    action: {
      success: 'stroke-success-600',
      error: 'stroke-error-600',
      info: 'stroke-info-600',
      warning: 'stroke-warning-600',
    },
  },
});

export type ToasterProps = {
  id: string;
  title: string;
  message?: string;
  action: 'error' | 'success' | 'info' | 'warning';
  icon?: LucideIcon;
  /**
   * An icon already drawn, for one a LucideIcon cannot be, such as a filled mask with its eyes
   * cut out. It takes the place of `icon` and carries its own color, so the toast does not
   * tint it.
   */
  iconElement?: ReactNode;
  /** Pass `polite` for a confirmation that should not interrupt a screen reader. */
  liveRegion?: 'polite' | 'assertive';
  close: (id: string) => void;
};

/**
 * The standard toast. A tap anywhere on it closes it, and so does an upward
 * swipe, which is the gesture a top-anchored toast invites. The swipe slides it
 * off and only then hands it to the toast layer to remove, so the layer's own
 * exit fade does not replay on a toast that has already left.
 *
 * A screen reader cannot swipe, so the toast says it is a button that dismisses
 * and publishes the same dismissal as an accessibility action, as the
 * announcement banner does.
 */
export function Toaster({
  id,
  action,
  title,
  message,
  icon,
  iconElement,
  liveRegion,
  close,
}: ToasterProps) {
  const toastId = 'toast-' + id;

  const { panHandlers, animatedStyle, onLayout, dismissByAction } = useSwipeUpDismiss({
    onExited: () => close(id),
  });

  return (
    <Animated.View {...panHandlers} style={animatedStyle} onLayout={onLayout}>
      <Toast
        nativeID={toastId}
        action={action}
        variant="outline"
        className="mx-5 flex-row gap-6 p-4 shadow-hard-5">
        <Pressable
          className="w-full flex-row items-center justify-center gap-3 web:max-w-md"
          onPress={() => close(id)}
          accessibilityActions={[{ name: DISMISS_ACTION_NAME, label: 'Dismiss' }]}
          onAccessibilityAction={(event) => {
            if (event.nativeEvent.actionName === DISMISS_ACTION_NAME) {
              dismissByAction();
            }
          }}
          {...getAccessibilityProps({ role: 'button', hint: 'Dismisses this message' })}>
          {iconElement ? (
            <View className="mt-0.5">{iconElement}</View>
          ) : (
            <Icon as={icon || HelpCircleIcon} className={iconStyle({ action })} />
          )}

          <VStack space="xs" className="flex-1">
            <ToastTitle
              size="md"
              liveRegion={liveRegion}
              className="font-semibold text-typography-900">
              {title}
            </ToastTitle>

            {message ? (
              <StyledLinkify
                linkClassName="underline"
                onPress={(url) => {
                  Linking.openURL(url);
                }}>
                <ToastDescription>{message}</ToastDescription>
              </StyledLinkify>
            ) : null}
          </VStack>
        </Pressable>
      </Toast>
    </Animated.View>
  );
}
